import { useCallback } from "react";

// Toggle helpers that operate purely on a statement object plus selection state.
// Expects `statement` to have: invoiceGroups, creditTransactionIds, invoicesRequestedAreMandatory.
export const useInvoiceToggle = (
  statement,
  selectedInvoiceIds,
  setSelectedInvoiceIds
) => {
  // Helper function to calculate required IDs
  const getRequiredIds = useCallback(() => {
    if (!statement?.invoiceGroups) return new Set();

    return new Set([
      ...(statement.creditTransactionIds || []),
      ...statement.invoiceGroups
        .filter((g) => g.required)
        .flatMap((g) => [
          ...g.invoices.filter((i) => !i.isDisputed).map((i) => i.ledgerTransactionId),
          ...g.creditNotes.map((cn) => cn.ledgerTransactionId),
          ...g.invoices
            .filter((i) => !i.isDisputed)
            .map((i) => [
              i.ledgerTransactionId,
              ...(i.creditNotes?.map((cn) => cn.ledgerTransactionId) || []),
            ])
            .flat(),
        ]),
    ]);
  }, [statement]);

  const toggleInvoice = useCallback(
    (invoiceId) => {
      if (!statement?.invoiceGroups || !selectedInvoiceIds) return;

      setSelectedInvoiceIds((current) => {
        const requiredIds = getRequiredIds();

        const invoice = statement.invoiceGroups
          .flatMap((g) => g.invoices)
          .find((inv) => inv.ledgerTransactionId === invoiceId);

        // Guard: disputed invoices cannot be toggled
        if (invoice?.isDisputed) return current;

        // Guard: invoices from required groups cannot be deselected
        if (
          statement.invoicesRequestedAreMandatory &&
          requiredIds.has(invoiceId)
        )
          return current;

        const next = new Set(current);
        const isSelected = next.has(invoiceId);

        if (invoice) {
          // Toggle the invoice
          isSelected ? next.delete(invoiceId) : next.add(invoiceId);

          // Toggle associated credit notes
          if (invoice.creditNotes) {
            invoice.creditNotes.forEach((cn) => {
              if (
                !statement.invoicesRequestedAreMandatory ||
                !requiredIds.has(cn.ledgerTransactionId)
              ) {
                isSelected
                  ? next.delete(cn.ledgerTransactionId)
                  : next.add(cn.ledgerTransactionId);
              }
            });
          }
        } else {
          // If it's not an invoice, just toggle the single item
          isSelected ? next.delete(invoiceId) : next.add(invoiceId);
        }

        return next;
      });
    },
    [statement, selectedInvoiceIds, setSelectedInvoiceIds, getRequiredIds]
  );

  const toggleGroup = useCallback(
    (ids) => {
      if (!statement?.invoiceGroups || !selectedInvoiceIds) return;

      setSelectedInvoiceIds((current) => {
        const requiredIds = getRequiredIds();

        const disputedIds = new Set(
          statement.invoiceGroups
            .flatMap((g) => g.invoices)
            .filter((inv) => inv.isDisputed)
            .map((inv) => inv.ledgerTransactionId)
        );

        // Only operate on invoices that are not required and not disputed
        const toggleableIds = ids.filter(
          (id) =>
            !disputedIds.has(id) &&
            (!statement.invoicesRequestedAreMandatory || !requiredIds.has(id))
        );
        const every = toggleableIds.every((id) => current.has(id));
        const next = new Set(current);

        // Get all invoices in this group
        const groupInvoices = statement.invoiceGroups
          .flatMap((g) => g.invoices)
          .filter((inv) => toggleableIds.includes(inv.ledgerTransactionId));

        // Toggle each invoice and its associated credit notes
        toggleableIds.forEach((id) => {
          const invoice = groupInvoices.find(
            (inv) => inv.ledgerTransactionId === id
          );
          if (invoice) {
            // Toggle the invoice
            every ? next.delete(id) : next.add(id);

            // Toggle associated credit notes if they exist
            if (invoice.creditNotes) {
              invoice.creditNotes.forEach((cn) => {
                if (
                  !statement.invoicesRequestedAreMandatory ||
                  !requiredIds.has(cn.ledgerTransactionId)
                ) {
                  every
                    ? next.delete(cn.ledgerTransactionId)
                    : next.add(cn.ledgerTransactionId);
                }
              });
            }
          } else {
            // If it's not an invoice, just toggle the single item
            every ? next.delete(id) : next.add(id);
          }
        });

        return next;
      });
    },
    [statement, selectedInvoiceIds, setSelectedInvoiceIds, getRequiredIds]
  );

  return { toggleInvoice, toggleGroup };
};
